/**
 * The canonical schema descriptor (TO-414 design D4): the platform-level shape
 * of one element type that import definitions map files onto. A published
 * `name@version` is a contract between its owning domain and every import
 * definition that targets it — never changed or removed once published.
 */

export enum CanonicalFieldType {
	STRING = 'string',
	INTEGER = 'integer',
	DECIMAL = 'decimal',
	BOOLEAN = 'boolean',
	DATE = 'date',
	DATETIME = 'datetime',
}

export interface CanonicalField {
	readonly name: string
	readonly type: CanonicalFieldType
	/** The field must be produced by every mapped record. */
	readonly required: boolean
	readonly nullable: boolean
	/** Maximum length in code points, for `string` fields; `null` otherwise. */
	readonly maxLength: number | null
	/**
	 * One value per locale (TO-404 BR-1): the field is only ever named
	 * `<name>.<locale>`, never alone. Absent means not localized.
	 */
	readonly localized?: boolean
}

export interface CanonicalSchema {
	readonly name: string
	readonly version: number
	/** The bounded context that owns, and alone may publish, this schema. */
	readonly owner: string
	readonly fields: readonly CanonicalField[]
}

/** Freezes a descriptor and everything inside it. */
export const deepFreeze = <T>(value: T): T => {
	if (typeof value === 'object' && value !== null && !Object.isFrozen(value)) {
		Object.freeze(value)
		for (const inner of Object.values(value)) deepFreeze(inner)
	}
	return value
}

/** A canonical field name resolved against its schema (TO-404 BR-1). */
export interface ResolvedCanonicalField {
	readonly field: CanonicalField
	/** The locale of a localized field's name; `null` for a plain field. */
	readonly locale: string | null
}

/**
 * The locale part of a localized field name: a lower-case 2–3 letter language
 * and optional `-`-separated 2–8 character subtags, at most
 * {@link CANONICAL_LOCALE_MAX} characters. Mirrors the catalog's locale tag
 * (`@today/models` `LOCALE_TAG_PATTERN`), which this package cannot import.
 */
export const CANONICAL_LOCALE_PATTERN = /^[a-z]{2,3}(-[A-Za-z0-9]{2,8})*$/
export const CANONICAL_LOCALE_MAX = 35

/**
 * Resolves a canonical field name — `name` for a plain field,
 * `<localizedField>.<locale>` for a localized one — or `undefined` when it
 * names no field of the schema (TO-404 BR-1). Every stage that names
 * canonical fields (F5 checks, F7 mapping, F8 validation, F9 diff) goes
 * through here.
 */
export const resolveCanonicalField = (schema: CanonicalSchema, name: string): ResolvedCanonicalField | undefined => {
	const dot = name.indexOf('.')
	if (dot < 0) {
		const field = schema.fields.find((f) => f.name === name && f.localized !== true)
		return field ? { field, locale: null } : undefined
	}
	const locale = name.slice(dot + 1)
	if (locale.length > CANONICAL_LOCALE_MAX || !CANONICAL_LOCALE_PATTERN.test(locale)) return undefined
	const field = schema.fields.find((f) => f.name === name.slice(0, dot) && f.localized === true)
	return field ? { field, locale } : undefined
}
